/**
 * Mantener pulsado.
 *
 * En un juego de cartas hacen falta dos gestos sobre la misma carta: uno para
 * jugarla y otro para leerla. Tocar ya juega, así que leer tiene que ser algo
 * distinto — y mantener pulsado es lo que la gente ya prueba por instinto,
 * porque es lo que hacen Hearthstone y Marvel Snap.
 *
 * Se implementa con eventos de puntero y no con `onContextMenu` porque el menú
 * contextual del navegador móvil aparece encima y tapa justo lo que se quería
 * mirar.
 */

import { useCallback, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/** Cuánto hay que aguantar. Por debajo de 350 ms se dispara al jugar una carta. */
const HOLD_MS = 400;
/** Si el dedo se mueve más que esto, era un arrastre y no una pulsación. */
const MOVE_TOLERANCE_PX = 12;

export interface LongPressHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerUp: () => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerCancel: () => void;
  onContextMenu: (event: { preventDefault: () => void }) => void;
}

/**
 * Devuelve los manejadores a repartir sobre el elemento.
 *
 * `onLongPress` recibe true cuando se cumple el tiempo. El componente decide qué
 * hacer; aquí no se sabe qué carta es.
 */
export function useLongPress(onLongPress: () => void, enabled = true): LongPressHandlers {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const origin = useRef<{ x: number; y: number } | null>(null);

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = undefined;
    origin.current = null;
  }, []);

  const start = useCallback(
    (event: ReactPointerEvent) => {
      if (!enabled) return;
      origin.current = { x: event.clientX, y: event.clientY };
      timer.current = setTimeout(() => {
        onLongPress();
        // Se limpia el origen para que el pointerup posterior no cuente como
        // toque: si no, soltar tras leer la carta la jugaría.
        origin.current = null;
      }, HOLD_MS);
    },
    [enabled, onLongPress],
  );

  const move = useCallback(
    (event: ReactPointerEvent) => {
      if (!origin.current) return;
      const distance = Math.hypot(
        event.clientX - origin.current.x,
        event.clientY - origin.current.y,
      );
      if (distance > MOVE_TOLERANCE_PX) cancel();
    },
    [cancel],
  );

  return {
    onPointerDown: start,
    onPointerUp: cancel,
    onPointerMove: move,
    onPointerCancel: cancel,
    // El menú contextual del navegador aparecería encima de la carta ampliada.
    onContextMenu: (event) => event.preventDefault(),
  };
}
